import PropTypes from 'prop-types';
import { useCarbonChain } from '@/stores/analysisStore';
import { numberFormat } from '@/utils/unitUtils';
import { smartRound } from '@/utils/miscUtils';
import Tooltip from '@/components/core/Tooltip';

const TOOLTIP_TEXT = `Costs here are levelized by tonnes of CO₂ captured, not by the analyzed node's product, so every stage is priced in the same denominator.

CO₂ lost along the way — liquefaction losses, pipeline leakage, storage leakage — is reported per stage but does not change that denominator.`;

const massLabel = quantity => (quantity ? `${numberFormat(quantity.value, 3)} ${quantity.unit}` : '—');

const StageRow = ({ stage }) => (
  <tr className="border-b border-gray-100">
    <td className="py-2.5 text-sm font-medium text-gray-900">{stage.label}</td>
    <td className="py-2.5 text-sm text-right font-medium tabular-nums text-gray-900">
      {numberFormat(stage.cost.value, 2)}
    </td>
    <td className="py-2.5 text-sm text-right tabular-nums text-gray-500 w-20">
      {stage.share == null ? '—' : `${smartRound(stage.share * 100)}%`}
    </td>
    <td className="py-2.5 text-sm text-right tabular-nums text-gray-500">{massLabel(stage.co2In)}</td>
    <td className="py-2.5 text-sm text-right tabular-nums text-gray-500">{massLabel(stage.co2Out)}</td>
    <td className="py-2.5 text-sm text-right tabular-nums text-gray-500">
      {stage.co2Lost && stage.co2Lost.value > 0 ? massLabel(stage.co2Lost) : '—'}
    </td>
  </tr>
);

StageRow.propTypes = {
  stage: PropTypes.object.isRequired,
};

const CarbonChainSection = () => {
  const plot = useCarbonChain();
  const results = plot?.results;

  // only capture-connected nodes have a chain; everything else gets no section
  if (!results) {
    return null;
  }

  const { total, stages, captured, stored, co2Lost, products, captureIncludedInPlantCosts } = results;

  return (
    <section className="py-4 border-t border-gray-200 flex flex-col gap-4">
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            Carbon capture &amp; handling
            <Tooltip label="Carbon capture & handling" text={TOOLTIP_TEXT} />
          </h3>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="flex flex-col gap-0.5">
          <div className="text-[11px] text-gray-500">Total capture &amp; handling</div>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-semibold text-gray-900 tabular-nums">{numberFormat(total.value, 2)}</span>
            <span className="text-base text-gray-500">{total.unit}</span>
          </div>
          <div className="text-xs text-gray-500">
            per tonne captured · captured {massLabel(captured)}
            {stored ? ` · stored ${massLabel(stored)}` : ''}
            {co2Lost && co2Lost.value > 0 ? ` · lost ${massLabel(co2Lost)}` : ''}
          </div>
        </div>
        {products.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="text-[11px] text-gray-500">Contribution to levelized cost</div>
            {products.map(product => (
              <div key={product.ioName} className="flex items-baseline gap-2 text-sm">
                <span className="text-gray-500 min-w-24">{product.label}</span>
                <span className="font-semibold tabular-nums text-gray-900">{numberFormat(product.cost.value, 2)}</span>
                <span className="text-xs text-gray-500">{product.cost.unit}</span>
                {product.share != null && (
                  <span className="text-xs text-gray-500">
                    ({smartRound(product.share * 100)}% of {numberFormat(product.levelizedCost.value, 2)})
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-2 text-left font-normal">Stage</th>
            <th className="py-2 text-right font-normal whitespace-nowrap">{total.unit}</th>
            <th className="py-2 text-right font-normal w-20">Share</th>
            <th className="py-2 text-right font-normal">CO₂ in</th>
            <th className="py-2 text-right font-normal">CO₂ out</th>
            <th className="py-2 text-right font-normal">Lost</th>
          </tr>
        </thead>
        <tbody>
          {stages.map(stage => (
            <StageRow key={stage.nodeId} stage={stage} />
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-300">
            <td className="py-3 text-sm font-semibold text-gray-900">Total</td>
            <td className="py-3 text-sm font-semibold text-right tabular-nums text-gray-900">
              {numberFormat(total.value, 2)}
            </td>
            <td className="py-3 text-sm font-semibold text-right text-gray-500">100%</td>
            <td colSpan={3}></td>
          </tr>
        </tfoot>
      </table>

      {!stored && stages.length > 0 && (
        <p className="text-xs text-base-content/60">
          This chain does not end in a storage module, so none of the CO₂ is reported as stored — the last stage still
          has a CO₂ stream, it just is not connected to anything.
        </p>
      )}

      {captureIncludedInPlantCosts && (
        <p className="text-xs text-base-content/60">
          Capture is integrated into this node, so its cost is already inside the node&apos;s own capital and operating
          lines and cannot be shown as a separate stage. The stages above start after capture.
        </p>
      )}
    </section>
  );
};

export default CarbonChainSection;
